import { useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../api/client';

const FORMAT_SPEC = `Формат JSON для пакетного импорта

Корень объекта:
  "import_mode" — "create" | "upsert" (по умолчанию create).
  "tasks" (обязательно) — массив задач.
  "defaults" (необязательно) — общие поля; в задаче пустые значения дополняются из defaults.

Идентичность задачи и повторный импорт:
  После слияния с defaults у каждой задачи должны быть непустыми source_name и source_number —
  по этой паре система находит задачу при import_mode: "upsert" и обновляет её.
  Режим "create": если такая пара уже есть в базе, задача пропускается.

Поля задачи (важное):
  source_name, source_number — обязательны для импорта (строки, без пустых после слияния).
  slug — опционально; человекочитаемый сегмент URL (латиница, цифры, дефис). Должен быть уникален среди заданных slug. Если не указан, ссылка на задачу в интерфейсе строится по числовому id.
  title — название; если пусто, подставится «{source_name} №{source_number}».
  statement, answer_type, expected_answer, status, source_author, year, answer_video, task_tags, hints, test_cases — как раньше.

task_tags: [ { "tag_slug": "ege-27", "order_in_tag": 0, "level_in_tag": "стандартный" } ],
  level_in_tag: базовый | стандартный | повышенный.

Пример:

{
  "import_mode": "upsert",
  "defaults": {
    "source_name": "ЕГЭ-7",
    "status": "published",
    "task_tags": [{ "tag_slug": "ege" }, { "tag_slug": "task-7" }]
  },
  "tasks": [
    {
      "source_number": "1",
      "slug": "ege7-1",
      "title": "ЕГЭ-7 №1",
      "statement": "...",
      "answer_type": "number",
      "expected_answer": "24"
    }
  ]
}`;

const AI_PROMPT = `Сгенерируй один валидный JSON для пакетного импорта задач в платформу diobuch.

Обязательно:
1) Корень: "import_mode": "create" или "upsert", массив "tasks", опционально "defaults".
2) Каждая задача после слияния с defaults имеет непустые "source_name" и "source_number" — пара задаёт одну и ту же задачу при повторном импорте (upsert).
3) "slug" опционален (латиница, цифры, дефисы; уникален). Без slug ссылка будет по id.
4) "title"; если нет — пусть будет краткое название с номером.
5) "statement" (markdown/текст), "answer_type": code|text|number|file|single_choice|multiple_choice|match|order|matrix|weighted_choice, "expected_answer" для number/text.
6) "task_tags": [ {"tag_slug": "..."} ] — теги из предметной области (ege, task-7, polyakov и т.д.).

Выведи только JSON, без комментариев до или после.`;

/** @param {string} text */
function parseImportFileJson(text) {
  try {
    const data = JSON.parse(text);
    if (!data || typeof data !== 'object') {
      return { data: null, parseError: 'Корень должен быть объектом.' };
    }
    return { data, parseError: null };
  } catch (e) {
    return { data: null, parseError: e.message || 'Ошибка разбора JSON' };
  }
}

function readFileAsText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result || '');
    reader.onerror = () => reject(reader.error || new Error('Ошибка чтения файла'));
    reader.readAsText(file, 'UTF-8');
  });
}

export default function BulkImportPage() {
  const [pasteText, setPasteText] = useState('');
  const [file, setFile] = useState(null);
  /** Очередь только при выборе 2+ файлов: { name, data?, parseError? } */
  const [fileQueue, setFileQueue] = useState([]);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [parseError, setParseError] = useState('');
  const [importResult, setImportResult] = useState(null);
  const [multiImportResult, setMultiImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [multiImportProgress, setMultiImportProgress] = useState(null);
  const [previewMode, setPreviewMode] = useState('compact');
  const [importMode, setImportMode] = useState('create');
  const [preflightLoading, setPreflightLoading] = useState(false);
  const [preflightResult, setPreflightResult] = useState(null);

  const isMultiFileMode = fileQueue.length >= 2;

  const fileQueueStats = useMemo(() => {
    const valid = fileQueue.filter((q) => q.data && !q.parseError);
    const taskSum = valid.reduce((acc, q) => {
      const n = Array.isArray(q.data.tasks) ? q.data.tasks.length : 0;
      return acc + n;
    }, 0);
    const parseErrors = fileQueue.filter((q) => q.parseError).length;
    return { fileCount: fileQueue.length, taskSum, parseErrors, validCount: valid.length };
  }, [fileQueue]);

  const clearInput = useCallback(() => {
    setPasteText('');
    setImportResult(null);
    setMultiImportResult(null);
    setPreflightResult(null);
    setParseError('');
    setFile(null);
    setFileQueue([]);
    setMultiImportProgress(null);
    setFileInputKey((k) => k + 1);
  }, []);

  const parsed = useMemo(() => {
    const text = pasteText.trim();
    if (!text) return { tasks: [], error: '', data: null };
    try {
      const data = JSON.parse(text);
      if (!data || typeof data !== 'object') return { tasks: [], error: 'Корень должен быть объектом.', data: null };
      const tasks = Array.isArray(data.tasks) ? data.tasks : [];
      return { data, tasks, error: '' };
    } catch (e) {
      return { tasks: [], error: e.message || 'Ошибка разбора JSON', data: null };
    }
  }, [pasteText]);

  const preview = useMemo(() => {
    if (!parsed.data || parsed.error) return null;
    const defaults = parsed.data?.defaults && typeof parsed.data.defaults === 'object' ? parsed.data.defaults : {};
    const tasks = Array.isArray(parsed.data?.tasks) ? parsed.data.tasks : [];

    const mergedTasks = tasks.map((task = {}, idx) => {
      const merged = { ...defaults, ...task };
      const taskTags = Array.isArray(merged.task_tags) ? merged.task_tags : [];
      const normalizedTags = taskTags
        .map((tt) => (tt?.tag_slug || tt?.slug || '').toString().trim())
        .filter(Boolean);
      const statement = (merged.statement || '').toString().trim();
      const statementShort = statement.length > 180 ? `${statement.slice(0, 180)}…` : statement;
      const hintsCount = Array.isArray(merged.hints) ? merged.hints.length : 0;
      const testsCount = Array.isArray(merged.test_cases) ? merged.test_cases.length : 0;
      const attachmentsCount = Array.isArray(merged.statement_attachments) ? merged.statement_attachments.length : 0;
      const videosCount = Array.isArray(merged.answer_video) ? merged.answer_video.filter((v) => typeof v === 'string' && v.trim()).length : 0;

      return {
        idx,
        slug: (merged.slug || '').toString(),
        title: (
          merged.title ||
          merged.slug ||
          `${(merged.source_name || '').toString()} №${(merged.source_number || '').toString()}`.trim() ||
          ''
        ).toString(),
        answerType: (merged.answer_type || 'code').toString(),
        status: (merged.status || 'draft').toString(),
        sourceAuthor: (merged.source_author || '').toString(),
        sourceName: (merged.source_name || '').toString(),
        sourceNumber: (merged.source_number || '').toString(),
        year: merged.year ?? null,
        tags: normalizedTags,
        statementShort,
        hintsCount,
        testsCount,
        attachmentsCount,
        videosCount,
      };
    });

    const uniqueTagSlugs = Array.from(new Set(mergedTasks.flatMap((t) => t.tags))).sort();
    const totalHints = mergedTasks.reduce((acc, t) => acc + t.hintsCount, 0);
    const totalTests = mergedTasks.reduce((acc, t) => acc + t.testsCount, 0);
    const totalAttachments = mergedTasks.reduce((acc, t) => acc + t.attachmentsCount, 0);
    const totalVideos = mergedTasks.reduce((acc, t) => acc + t.videosCount, 0);

    return {
      tasks: mergedTasks,
      uniqueTagSlugs,
      totalHints,
      totalTests,
      totalAttachments,
      totalVideos,
    };
  }, [parsed]);

  const importStatus = useMemo(() => {
    if (multiImportResult) {
      const { totalUploaded, totalSkipped, totalErrors, hadParseFailures, hadRequestFailures } = multiImportResult;
      if (hadParseFailures || hadRequestFailures || totalErrors > 0) {
        return {
          text: `Импорт файлов: загружено ${totalUploaded}, пропущено ${totalSkipped}, ошибок ${totalErrors}`,
          tone: totalErrors > 0 || hadRequestFailures ? 'error' : 'warning',
        };
      }
      if (totalSkipped > 0) {
        return {
          text: `Импорт завершён: загружено ${totalUploaded}, пропущено ${totalSkipped}`,
          tone: 'warning',
        };
      }
      return {
        text: `Загружено задач: ${totalUploaded} (${multiImportResult.lines.length} файл(ов))`,
        tone: 'success',
      };
    }
    if (!importResult) return null;
    const createdCount = importResult.created?.length || 0;
    const updatedCount = importResult.updated?.length || 0;
    const uploadedCount = createdCount + updatedCount;
    const skippedCount = importResult.skipped?.length || 0;
    const errorCount = importResult.errors?.length || 0;

    if (errorCount > 0) {
      return {
        text: `Ошибки импорта: ${errorCount}`,
        tone: 'error',
      };
    }
    if (skippedCount > 0) {
      return {
        text: `Импорт завершён: загружено ${uploadedCount}, пропущено ${skippedCount}`,
        tone: 'warning',
      };
    }
    return {
      text: `Загружено ${uploadedCount} задач`,
      tone: 'success',
    };
  }, [importResult, multiImportResult]);

  const loadFiles = async (ev) => {
    const list = ev.target.files;
    if (!list?.length) {
      setFile(null);
      setFileQueue([]);
      return;
    }
    setImportResult(null);
    setMultiImportResult(null);
    setPreflightResult(null);
    setParseError('');

    if (list.length === 1) {
      const f = list[0];
      setFile(f);
      setFileQueue([]);
      try {
        const text = await readFileAsText(f);
        setPasteText(text);
      } catch (e) {
        setPasteText('');
        setParseError(e.message || 'Ошибка чтения файла');
      }
      return;
    }

    setFile(null);
    setPasteText('');
    const items = [];
    const filesArr = Array.from(list);
    for (const f of filesArr) {
      try {
        const text = await readFileAsText(f);
        const { data, parseError: pe } = parseImportFileJson(text);
        items.push({ name: f.name, data, parseError: pe });
      } catch (e) {
        items.push({ name: f.name, data: null, parseError: e.message || 'Ошибка чтения файла' });
      }
    }
    setFileQueue(items);
  };

  const runPreflight = useCallback(async () => {
    if (parsed.error || !parsed.data) {
      setParseError(parsed.error || 'Вставьте или загрузите JSON.');
      return;
    }
    setPreflightLoading(true);
    setPreflightResult(null);
    setParseError('');
    try {
      const payload = { ...parsed.data, import_mode: importMode };
      const res = await api.post('/tasks/editor/import/preflight/', payload);
      setPreflightResult(res.data);
    } catch (e) {
      const data = e.response?.data;
      if (data && typeof data === 'object') {
        setPreflightResult(data);
      } else {
        setPreflightResult({ ok: false, errors: [e.message || 'Ошибка запроса'] });
      }
    } finally {
      setPreflightLoading(false);
    }
  }, [parsed.data, parsed.error, importMode]);

  const runImport = async () => {
    if (parsed.error || !parsed.data) {
      setParseError(parsed.error || 'Вставьте или загрузите JSON.');
      return;
    }
    setImporting(true);
    setImportResult(null);
    setMultiImportResult(null);
    setParseError('');
    try {
      const payload = { ...parsed.data, import_mode: importMode };
      const res = await api.post('/tasks/editor/import/', payload);
      const nextResult = {
        created: res.data.created || [],
        updated: res.data.updated || [],
        skipped: res.data.skipped || [],
        errors: res.data.errors || [],
      };
      setImportResult(nextResult);
      if (!nextResult.errors.length) {
        setPasteText('');
        setFile(null);
        setFileInputKey((k) => k + 1);
        setPreflightResult(null);
        setParseError('');
      }
    } catch (e) {
      const data = e.response?.data;
      setImportResult({
        created: data?.created || [],
        updated: data?.updated || [],
        skipped: data?.skipped || [],
        errors: data?.errors || [e.message || 'Ошибка запроса'],
      });
    } finally {
      setImporting(false);
    }
  };

  const runMultiImport = async () => {
    if (fileQueue.length < 2) return;
    setImporting(true);
    setImportResult(null);
    setMultiImportResult(null);
    setParseError('');
    const lines = [];
    let totalUploaded = 0;
    let totalSkipped = 0;
    let totalErrors = 0;
    let hadParseFailures = false;
    let hadRequestFailures = false;

    try {
      for (let i = 0; i < fileQueue.length; i += 1) {
        const item = fileQueue[i];
        setMultiImportProgress({ current: i + 1, total: fileQueue.length, name: item.name });

        if (item.parseError) {
          hadParseFailures = true;
          lines.push(`${item.name}: не импортировано — ${item.parseError}`);
          continue;
        }

        const taskArr = Array.isArray(item.data.tasks) ? item.data.tasks : [];
        if (taskArr.length === 0) {
          lines.push(`${item.name}: нет задач в JSON (поле tasks пустое или отсутствует)`);
          continue;
        }

        const payload = { ...item.data, import_mode: importMode };
        try {
          const res = await api.post('/tasks/editor/import/', payload);
          const created = res.data.created || [];
          const updated = res.data.updated || [];
          const skipped = res.data.skipped || [];
          const errors = res.data.errors || [];
          const uploaded = created.length + updated.length;
          totalUploaded += uploaded;
          totalSkipped += skipped.length;
          totalErrors += errors.length;
          lines.push(
            `${item.name}: загружено ${uploaded}, пропущено ${skipped.length}, ошибок ${errors.length}`,
          );
        } catch (e) {
          const data = e.response?.data;
          if (data && typeof data === 'object') {
            const created = data.created || [];
            const updated = data.updated || [];
            const skipped = data.skipped || [];
            const errors = data.errors || [];
            const uploaded = created.length + updated.length;
            totalUploaded += uploaded;
            totalSkipped += skipped.length;
            totalErrors += errors.length;
            lines.push(
              `${item.name}: загружено ${uploaded}, пропущено ${skipped.length}, ошибок ${errors.length}`,
            );
          } else {
            hadRequestFailures = true;
            totalErrors += 1;
            lines.push(`${item.name}: ошибка запроса — ${e.message || 'неизвестная ошибка'}`);
          }
        }
      }

      const summary = {
        lines,
        totalUploaded,
        totalSkipped,
        totalErrors,
        hadParseFailures,
        hadRequestFailures,
      };
      setMultiImportResult(summary);

      const allOk =
        !hadParseFailures &&
        !hadRequestFailures &&
        totalErrors === 0;
      if (allOk) {
        setFileQueue([]);
        setFile(null);
        setPasteText('');
        setFileInputKey((k) => k + 1);
        setPreflightResult(null);
        setParseError('');
      }
    } finally {
      setImporting(false);
      setMultiImportProgress(null);
    }
  };

  const handleImportClick = () => {
    if (isMultiFileMode) {
      void runMultiImport();
    } else {
      void runImport();
    }
  };

  return (
    <div className="bulk-import-page">
      <div className="page-header">
        <Link to="/teacher/tasks" className="btn btn-outline btn-sm">← К списку задач</Link>
        <h1>Пакетный импорт задач</h1>
        <p className="page-subtitle">
          Загрузите один или несколько JSON с массивом задач (и опционально defaults). Несколько файлов импортируются по очереди.
          Теги задаются по slug существующих тегов.
        </p>
      </div>

      {/* Описание формата и промпт для ИИ */}
      <section className="editor-section bulk-import-docs">
        <h3>Формат JSON и промпт для ИИ</h3>
        <details className="format-spec">
          <summary>Спецификация формата</summary>
          <pre className="format-spec-pre">{FORMAT_SPEC}</pre>
        </details>
        <details className="format-spec">
          <summary>Промпт для ИИ (скопируй в ChatGPT / другой ИИ)</summary>
          <p className="text-secondary" style={{ marginBottom: '0.5rem' }}>
            Скопируй текст ниже и вставь в чат с ИИ вместе с текстом или описанием сборника задач. ИИ вернёт готовый JSON — вставь его в поле ниже и нажми «Импорт».
          </p>
          <pre className="format-spec-pre ai-prompt">{AI_PROMPT}</pre>
        </details>
      </section>

      <section className="editor-section">
        <h3>Вставка JSON или загрузка файлов</h3>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
          <label className="text-secondary">
            Режим импорта:{' '}
            <select
              className="form-input"
              value={importMode}
              onChange={(e) => { setImportMode(e.target.value); setPreflightResult(null); }}
              style={{ width: 'auto', display: 'inline-block' }}
            >
              <option value="create">Добавление</option>
              <option value="upsert">Обновление (upsert)</option>
            </select>
          </label>
          <label className="text-secondary">
            Вид предпросмотра:{' '}
            <select
              className="form-input"
              value={previewMode}
              onChange={(e) => setPreviewMode(e.target.value)}
              style={{ width: 'auto', display: 'inline-block' }}
            >
              <option value="compact">Компактно</option>
              <option value="detailed">Подробно</option>
            </select>
          </label>
        </div>
        <p className="text-secondary" style={{ marginBottom: '0.5rem' }}>
          {importMode === 'create'
            ? 'Добавление: если пара source_name+source_number уже есть в базе, задача будет пропущена.'
            : 'Обновление (upsert): новые создаются, существующие находятся по source_name+source_number и перезаписываются. Коллизия slug с другой задачей — ошибка.'}
        </p>
        <textarea
          value={pasteText}
          onChange={(e) => {
            setPasteText(e.target.value);
            if (fileQueue.length > 0) {
              setFileQueue([]);
              setFileInputKey((k) => k + 1);
            }
            setImportResult(null);
            setMultiImportResult(null);
            setPreflightResult(null);
            setParseError('');
          }}
          rows={14}
          className="form-input font-mono"
          placeholder='{"tasks": [...], "defaults": {...}}'
        />
        <div style={{ marginTop: '0.5rem', display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center' }}>
          <button
            type="button"
            className="btn btn-outline btn-sm"
            onClick={clearInput}
            disabled={
              !pasteText.trim() &&
              !file &&
              fileQueue.length === 0 &&
              !importResult &&
              !multiImportResult &&
              !preflightResult &&
              !parseError
            }
            title="Очистить поле и сбросить результаты"
          >
            Очистить
          </button>
        </div>
        <div style={{ marginTop: '0.5rem' }}>
          <input
            key={fileInputKey}
            type="file"
            accept=".json,.txt"
            multiple
            onChange={(e) => {
              void loadFiles(e);
            }}
          />
          {file && <span className="text-secondary"> Файл: {file.name}</span>}
          {isMultiFileMode && (
            <div className="text-secondary" style={{ marginTop: '0.35rem' }}>
              Выбрано файлов: {fileQueueStats.fileCount}
              {fileQueueStats.validCount > 0 && (
                <>
                  {' '}
                  — в сумме задач: {fileQueueStats.taskSum}
                  {fileQueueStats.parseErrors > 0 && `, с ошибкой разбора: ${fileQueueStats.parseErrors}`}
                </>
              )}
              {fileQueueStats.validCount === 0 && fileQueueStats.parseErrors > 0 && (
                <> — все файлы с ошибкой разбора JSON</>
              )}
            </div>
          )}
        </div>
        {isMultiFileMode && (
          <p className="text-secondary" style={{ marginTop: '0.5rem', fontSize: '0.95em' }}>
            Оценка на сервере (preflight) для нескольких файлов недоступна — используйте поле выше только для вставки одного JSON или выберите один файл.
          </p>
        )}
        <div style={{ marginTop: '0.75rem', display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center' }}>
          <button
            type="button"
            className="btn btn-primary"
            disabled={
              importing ||
              (isMultiFileMode
                ? false
                : !!parsed.error || !parsed.tasks?.length)
            }
            onClick={handleImportClick}
          >
            {importing && multiImportProgress
              ? `Импорт файла ${multiImportProgress.current} из ${multiImportProgress.total}…`
              : importing
                ? 'Импорт…'
                : 'Импорт'}
          </button>
          {importStatus && (
            <span
              className={
                importStatus.tone === 'error'
                  ? 'text-error'
                  : importStatus.tone === 'warning'
                    ? 'text-secondary'
                    : 'text-success'
              }
            >
              {importStatus.text}
            </span>
          )}
        </div>
        {parsed.error && <div className="alert alert-error" style={{ marginTop: '0.5rem' }}>{parsed.error}</div>}
      </section>

      {/* Предпросмотр */}
      {parsed.tasks?.length > 0 && !parsed.error && preview && (
        <section className="editor-section">
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
            <h3 style={{ margin: 0 }}>Предпросмотр: задач к импорту — {parsed.tasks.length}</h3>
            <button
              type="button"
              className="btn btn-outline btn-sm"
              disabled={
                isMultiFileMode ||
                !!parsed.error ||
                !parsed.tasks?.length ||
                preflightLoading
              }
              onClick={runPreflight}
            >
              {preflightLoading ? 'Оценка…' : 'Оценка на сервере (без записи в БД)'}
            </button>
          </div>
          {preflightResult && (
            <div
              className="alert"
              style={{
                marginBottom: '1rem',
                background: preflightResult.ok
                  ? 'var(--success-bg, #d4edda)'
                  : 'var(--danger-bg, #f8d7da)',
                color: preflightResult.ok
                  ? 'var(--success, #155724)'
                  : 'var(--danger, #721c24)',
              }}
            >
              <strong>Результат оценки на сервере</strong> (текущий режим: {importMode === 'upsert' ? 'обновление' : 'добавление'})
              {preflightResult.ok && (
                <table className="bulk-preview-table" style={{ marginTop: '0.5rem' }}>
                  <tbody>
                    <tr><td><strong>Будет создано задач</strong></td><td>{preflightResult.tasks_to_create}</td></tr>
                    <tr><td><strong>Будет обновлено задач</strong></td><td>{preflightResult.tasks_to_update}</td></tr>
                    <tr><td><strong>Будет пропущено</strong></td><td>{preflightResult.tasks_skipped}</td></tr>
                    <tr><td><strong>Новых тегов (collection)</strong></td><td>{preflightResult.tags_to_create}</td></tr>
                    <tr><td><strong>Связей подсказок (новых Hint)</strong></td><td>{preflightResult.hints_to_create}</td></tr>
                    <tr><td><strong>Тест-кейсов</strong></td><td>{preflightResult.test_cases_to_create}</td></tr>
                  </tbody>
                </table>
              )}
              {preflightResult.new_tag_slugs?.length > 0 && (
                <div style={{ marginTop: '0.5rem' }}>
                  <span className="text-secondary">Новые slug тегов: </span>
                  <span style={{ fontFamily: 'monospace', fontSize: '0.9em' }}>
                    {preflightResult.new_tag_slugs.join(', ')}
                  </span>
                  {preflightResult.new_tag_slugs_truncated && (
                    <span className="text-secondary"> … (список обрезан)</span>
                  )}
                </div>
              )}
              {preflightResult.skipped?.length > 0 && (
                <ul style={{ marginTop: '0.5rem', paddingLeft: '1.2rem' }}>
                  {preflightResult.skipped.slice(0, 15).map((msg, i) => (
                    <li key={i}>{msg}</li>
                  ))}
                  {(preflightResult.skipped_truncated || preflightResult.skipped.length > 15) && (
                    <li className="text-secondary">… см. полный ответ API при необходимости</li>
                  )}
                </ul>
              )}
              {preflightResult.errors?.length > 0 && (
                <div className="alert alert-error" style={{ marginTop: '0.5rem' }}>
                  Ошибки валидации:
                  <ul style={{ marginTop: '0.35rem', paddingLeft: '1.2rem' }}>
                    {preflightResult.errors.map((err, i) => (
                      <li key={i}>{err}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
          <div className="bulk-preview-table-wrap" style={{ marginBottom: '1rem' }}>
            <table className="bulk-preview-table">
              <tbody>
                <tr>
                  <td><strong>Будет создано задач</strong></td>
                  <td>{preview.tasks.length}</td>
                </tr>
                <tr>
                  <td><strong>Тегов (уникальных slug)</strong></td>
                  <td>{preview.uniqueTagSlugs.length}</td>
                </tr>
                <tr>
                  <td><strong>Подсказок всего</strong></td>
                  <td>{preview.totalHints}</td>
                </tr>
                <tr>
                  <td><strong>Тест-кейсов всего</strong></td>
                  <td>{preview.totalTests}</td>
                </tr>
                <tr>
                  <td><strong>Вложений условия всего</strong></td>
                  <td>{preview.totalAttachments}</td>
                </tr>
                <tr>
                  <td><strong>Видеоразборов всего</strong></td>
                  <td>{preview.totalVideos}</td>
                </tr>
              </tbody>
            </table>
            <p className="text-secondary" style={{ marginTop: '0.5rem' }}>
              Теги из списка ниже будут привязаны к задачам. Если каких-то slug нет в базе, они автоматически создадутся как <code>collection</code>.
            </p>
            <div style={{ marginTop: '0.4rem', display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
              {preview.uniqueTagSlugs.slice(0, 60).map((slug) => (
                <span key={slug} className="tag-chip">{slug}</span>
              ))}
              {preview.uniqueTagSlugs.length > 60 && (
                <span className="text-secondary">… и ещё {preview.uniqueTagSlugs.length - 60}</span>
              )}
            </div>
          </div>

          <div className="bulk-preview-table-wrap">
            <table className="bulk-preview-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>slug</th>
                  <th>title</th>
                  {previewMode === 'detailed' && <th>условие (фрагмент)</th>}
                  <th>теги</th>
                  <th>answer_type</th>
                  <th>status</th>
                  {previewMode === 'detailed' && <th>доп. данные</th>}
                </tr>
              </thead>
              <tbody>
                {preview.tasks.slice(0, 50).map((t, i) => (
                  <tr key={i}>
                    <td>{i + 1}</td>
                    <td>{t.slug || '—'}</td>
                    <td>{(t.title || '').slice(0, 48)}{(t.title || '').length > 48 ? '…' : ''}</td>
                    {previewMode === 'detailed' && <td>{t.statementShort || '—'}</td>}
                    <td>{t.tags.length ? t.tags.slice(0, 5).join(', ') : '—'}{t.tags.length > 5 ? ` … +${t.tags.length - 5}` : ''}</td>
                    <td>{t.answerType}</td>
                    <td>{t.status}</td>
                    {previewMode === 'detailed' && (
                      <td>
                        {!!t.sourceAuthor && <>авт.: {t.sourceAuthor}<br /></>}
                        {!!t.sourceName && <>ист.: {t.sourceName}<br /></>}
                        {!!t.sourceNumber && <>№: {t.sourceNumber}<br /></>}
                        {t.year !== null && t.year !== undefined && <>год: {t.year}<br /></>}
                        h:{t.hintsCount} tc:{t.testsCount} att:{t.attachmentsCount} v:{t.videosCount}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            {preview.tasks.length > 50 && <p className="text-secondary">Показаны первые 50 из {preview.tasks.length}</p>}
          </div>
        </section>
      )}

      {parseError && <div className="alert alert-error">{parseError}</div>}

      {multiImportResult && (
        <section className="editor-section">
          <h3>Результат импорта (несколько файлов)</h3>
          <pre className="bulk-import-multi-log">{multiImportResult.lines.join('\n')}</pre>
          <p
            className={
              multiImportResult.totalErrors > 0 ||
              multiImportResult.hadParseFailures ||
              multiImportResult.hadRequestFailures
                ? 'text-secondary'
                : 'text-success'
            }
            style={{ marginTop: '0.75rem' }}
          >
            Всего: загружено {multiImportResult.totalUploaded}, пропущено {multiImportResult.totalSkipped},
            ошибок {multiImportResult.totalErrors}
          </p>
        </section>
      )}

      {importResult && !multiImportResult && (
        <section className="editor-section">
          <h3>Результат импорта</h3>
          {importResult.created?.length > 0 && (
            <div className="alert" style={{ background: 'var(--success-bg, #d4edda)', color: 'var(--success, #155724)' }}>
              Создано задач: {importResult.created.length}
              <ul style={{ marginTop: '0.5rem', paddingLeft: '1.2rem' }}>
                {importResult.created.slice(0, 20).map((t, i) => (
                  <li key={i}>{t.title || t.slug} (slug: {t.slug})</li>
                ))}
                {importResult.created.length > 20 && <li>… и ещё {importResult.created.length - 20}</li>}
              </ul>
            </div>
          )}
          {importResult.updated?.length > 0 && (
            <div className="alert" style={{ background: 'var(--info-bg, #d1ecf1)', color: 'var(--info, #0c5460)', marginTop: '0.75rem' }}>
              Обновлено задач: {importResult.updated.length}
              <ul style={{ marginTop: '0.5rem', paddingLeft: '1.2rem' }}>
                {importResult.updated.slice(0, 20).map((t, i) => (
                  <li key={i}>{t.title || t.slug} (slug: {t.slug})</li>
                ))}
                {importResult.updated.length > 20 && <li>… и ещё {importResult.updated.length - 20}</li>}
              </ul>
            </div>
          )}
          {importResult.skipped?.length > 0 && (
            <div className="alert" style={{ marginTop: '0.75rem', background: 'var(--warning-bg, #fff3cd)', color: 'var(--warning, #856404)' }}>
              Пропущено: {importResult.skipped.length}
              <ul style={{ marginTop: '0.5rem', paddingLeft: '1.2rem' }}>
                {importResult.skipped.slice(0, 30).map((msg, i) => (
                  <li key={i}>{msg}</li>
                ))}
                {importResult.skipped.length > 30 && <li>… и ещё {importResult.skipped.length - 30}</li>}
              </ul>
            </div>
          )}
          {importResult.errors?.length > 0 && (
            <div className="alert alert-error">
              Ошибки:
              <ul style={{ marginTop: '0.5rem', paddingLeft: '1.2rem' }}>
                {importResult.errors.map((err, i) => (
                  <li key={i}>{err}</li>
                ))}
              </ul>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
